import { z } from 'zod';
import { hostOf, PathPrefixSchema, TargetHostSchema, TargetUrlSchema } from '../domain/target.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
/**
 * The rules of engagement for one test: what may be reached, how hard, and when.
 *
 * Scope versions are **immutable and append-only** (§5). A change is a new version, never an
 * edit, so an approval granted against version 3 can never silently come to mean something
 * else. `Approval` records carry the `scopeVersion` they approved for exactly this reason.
 */
export const SCOPE_MAX_HOSTS = 20;
export const SCOPE_MAX_SEED_URLS = 50;
export const SCOPE_MAX_EXCLUDES = 100;
/**
 * A window during which testing is permitted, as absolute timestamps.
 *
 * Deliberately not a recurrence rule: "every Tuesday 02:00" has to be resolved against a
 * timezone and a DST calendar before it can gate anything, and a scanner that starts an hour
 * early because of a clock change is an incident. Absolute instants need no interpretation.
 */
export const TestWindowSchema = z
    .strictObject({
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
})
    .refine((w) => Date.parse(w.endsAt) > Date.parse(w.startsAt), {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
});
export const ScopeTargetsSchema = z.strictObject({
    allowedHosts: z.array(TargetHostSchema).min(1).max(SCOPE_MAX_HOSTS),
    seedUrls: z.array(TargetUrlSchema).min(1).max(SCOPE_MAX_SEED_URLS),
    excludePaths: z.array(PathPrefixSchema).max(SCOPE_MAX_EXCLUDES).default([]),
});
/**
 * Intensity ceilings.
 *
 * A DAST run against a live banking application is a load generator pointed at production-like
 * infrastructure. The rate ceiling is low on purpose: an engagement that takes longer is
 * recoverable, an engagement that takes the application down is an incident.
 */
export const ScopeLimitsSchema = z.strictObject({
    maxRequestsPerSecond: z.number().int().min(1).max(50),
    maxDurationMinutes: z.number().int().min(1).max(1440),
});
/**
 * What this scope ever permits, independently of the approval gates.
 *
 * Both default to false, so an intrusive capability is opt-in in writing. This is a ceiling,
 * not a licence: reaching `ACTIVE` still needs a recorded approval (§5). A scope may permit
 * active scanning and the engagement still be refused it.
 */
export const ScopePermitsSchema = z.strictObject({
    activeScan: z.boolean().default(false),
    fuzzing: z.boolean().default(false),
});
/** Who authorised the test, on the customer's side. Evidence, not authentication. */
export const ScopeAuthorisationSchema = z.strictObject({
    /** Change or CSA reference the authorisation is recorded under. */
    reference: z.string().trim().min(1).max(120),
    authorisedByName: z.string().trim().min(1).max(160),
    authorisedAt: IsoDateTimeSchema,
});
/**
 * PUT /v1/engagements/{id}/scope
 *
 * Creates the next version. Neither the version number nor the engagement is accepted from the
 * body — both are server-owned, so a client cannot overwrite an approved version by naming it.
 */
export const PutScopeRequestSchema = z
    .strictObject({
    targets: ScopeTargetsSchema,
    limits: ScopeLimitsSchema,
    windows: z.array(TestWindowSchema).min(1).max(20),
    permits: ScopePermitsSchema.default({ activeScan: false, fuzzing: false }),
    authorisation: ScopeAuthorisationSchema,
    /**
     * Where the scanner's test credentials live — an ARN, never the credentials. A secret in
     * a scope row would be readable by everyone who can read the engagement and would end up
     * in audit exports (§4).
     */
    credentialsSecretArn: z
        .string()
        .regex(/^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/, 'Must be a Secrets Manager ARN')
        .optional(),
    /** The version the client believes is current; omitted when creating the first. */
    expectedCurrentVersion: z.number().int().nonnegative().optional(),
})
    .refine((s) => s.targets.seedUrls.every((url) => s.targets.allowedHosts.includes(hostOf(url))), {
    // Otherwise the seed list quietly becomes a second, unreviewed host list.
    message: 'Every seed URL must be on a host listed in allowedHosts',
    path: ['targets', 'seedUrls'],
})
    .refine((s) => new Set(s.targets.allowedHosts).size === s.targets.allowedHosts.length, {
    message: 'allowedHosts contains duplicates',
    path: ['targets', 'allowedHosts'],
});
export const ScopeVersionSchema = z.object({
    engagementId: IdSchema,
    orgId: OrgIdSchema,
    version: z.number().int().positive(),
    targets: ScopeTargetsSchema,
    limits: ScopeLimitsSchema,
    windows: z.array(TestWindowSchema),
    permits: ScopePermitsSchema,
    authorisation: ScopeAuthorisationSchema,
    credentialsSecretArn: z.string().optional(),
    createdAt: IsoDateTimeSchema,
    createdBy: UserIdSchema,
    /**
     * SHA-256 of this version's canonical JSON, computed by the server *when read*, never stored.
     * Stored, it would be rewritten by whoever changed the scope; recomputed, it is compared with
     * the digest the approval recorded, which is what exposes a change made after approval.
     */
    digest: z.string().length(64).optional(),
});
export const ListScopeVersionsResponseSchema = z.object({
    items: z.array(ScopeVersionSchema),
});
/** DynamoDB sorts sort keys lexically, so the version is zero-padded (§5: `SCOPE#{version:0000}`). */
export function scopeSortKey(version) {
    return `SCOPE#${String(version).padStart(4, '0')}`;
}
/**
 * Whether `at` falls inside any permitted window.
 *
 * Shared so the console, the backend and any future scheduler agree. Used to *refuse* a scan
 * outside its window; it is never a reason to start one.
 */
export function isWithinWindow(windows, at) {
    const t = at.getTime();
    return windows.some((w) => Date.parse(w.startsAt) <= t && t <= Date.parse(w.endsAt));
}
//# sourceMappingURL=scope.js.map